// Secret 13 — Education Content Library
// 50 articles organized by category

export const EDUCATION_CATEGORIES = [
  { id: 'basics', label: 'The Basics', description: 'Start here. The essentials every member should know.' },
  { id: 'science', label: 'The Science', description: 'What happens beneath the surface.' },
  { id: 'terpenes', label: 'Terpenes & Flavor', description: 'The invisible architects of every experience.' },
  { id: 'methods', label: 'Consumption', description: 'How you choose to experience matters.' },
  { id: 'wellness', label: 'Wellness & Effects', description: 'Understanding what your body and mind feel.' },
  { id: 'lifestyle', label: 'Lifestyle & Culture', description: 'Cannabis in the context of a life well-lived.' },
  { id: 'safety', label: 'Safety & Responsibility', description: 'Enjoy intentionally. Always.' },
];

export const EDUCATION_ARTICLES = [
  // ── THE BASICS (10) ──────────────────────────────────────
  {
    slug: 'what-is-cannabis',
    category: 'basics',
    title: 'What Is Cannabis?',
    subtitle: 'The plant behind the experience.',
    readTime: '4 min',
    sections: [
      {
        heading: 'A Plant With History',
        body: 'Cannabis is a flowering plant that has been cultivated by humans for thousands of years. It belongs to the family Cannabaceae and is one of the oldest domesticated crops in recorded history. Archaeological evidence suggests its use dates back to at least 5,000 BCE in Central Asia.',
      },
      {
        heading: 'The Three Species',
        body: 'Cannabis is commonly divided into three species: Cannabis sativa, Cannabis indica, and Cannabis ruderalis. Sativa plants tend to be tall and narrow-leafed, indica plants shorter and broader, and ruderalis small and auto-flowering. However, modern genetics has blurred these lines considerably through centuries of crossbreeding.',
      },
      {
        heading: 'Beyond Recreation',
        body: 'The cannabis plant produces over 500 distinct chemical compounds, including more than 100 cannabinoids and hundreds of terpenes. These compounds interact with the human body in complex ways, offering a spectrum of effects that range from deep relaxation to heightened creativity.',
      },
      {
        heading: 'Why It Matters',
        body: 'Understanding the plant is the first step to understanding the experience. At Secret 13, we believe informed consumption leads to better moments. Every Secret in our collection is crafted with this knowledge at its foundation.',
      },
    ],
  },
  {
    slug: 'indica-vs-sativa',
    category: 'basics',
    title: 'Indica vs. Sativa',
    subtitle: 'The classic distinction — and why it\'s evolving.',
    readTime: '5 min',
    sections: [
      {
        heading: 'The Traditional View',
        body: 'For decades, the cannabis world was divided into two camps: indica for relaxation and body effects, sativa for energy and cerebral stimulation. This binary classification served as a simple entry point for consumers, but science has revealed a far more nuanced reality.',
      },
      {
        heading: 'The Modern Understanding',
        body: 'Today, researchers and cultivators recognize that the indica/sativa distinction primarily describes the plant\'s physical morphology — how it grows — rather than its effects. The actual experience is determined by the plant\'s unique chemical profile: its cannabinoids, terpenes, and flavonoids working together.',
      },
      {
        heading: 'The Chemotype Approach',
        body: 'A more accurate way to predict effects is through chemotyping — analyzing the specific chemical composition of each strain. Two plants that look identical can produce dramatically different experiences based on their terpene and cannabinoid ratios.',
      },
      {
        heading: 'Our Approach',
        body: 'At Secret 13, we classify our collection by experience rather than species. Each Secret is defined by its effect profile — what it makes you feel — because that\'s what actually matters when you\'re choosing your moment.',
      },
    ],
  },
  {
    slug: 'understanding-thc',
    category: 'basics',
    title: 'Understanding THC',
    subtitle: 'The compound everyone talks about.',
    readTime: '5 min',
    sections: [
      {
        heading: 'What THC Actually Is',
        body: 'Delta-9-tetrahydrocannabinol (THC) is the primary psychoactive compound in cannabis. It\'s responsible for the euphoria, altered perception, and mood changes associated with cannabis use. THC works by binding to CB1 receptors in the brain and central nervous system.',
      },
      {
        heading: 'How It Works',
        body: 'When THC enters your system, it mimics a naturally occurring neurotransmitter called anandamide — often called the "bliss molecule." By binding to the same receptors, THC amplifies signals related to pleasure, memory, coordination, and time perception.',
      },
      {
        heading: 'The Percentage Myth',
        body: 'Higher THC doesn\'t always mean a better experience. A 30% THC flower might feel less enjoyable than a 20% THC flower with a superior terpene profile. The entourage effect — the synergy between all compounds — matters more than any single number.',
      },
      {
        heading: 'Finding Your Range',
        body: 'Everyone\'s endocannabinoid system is unique. What feels perfect for one person might be overwhelming for another. We recommend starting with lower-THC options and gradually finding the range where the experience feels most natural and enjoyable for you.',
      },
    ],
  },
  {
    slug: 'what-is-cbd',
    category: 'basics',
    title: 'What Is CBD?',
    subtitle: 'The calming counterpart.',
    readTime: '4 min',
    sections: [
      {
        heading: 'CBD Explained',
        body: 'Cannabidiol (CBD) is the second most abundant cannabinoid in cannabis. Unlike THC, CBD is non-psychoactive — it won\'t produce a "high." Instead, it\'s known for its calming, anti-inflammatory, and anxiety-reducing properties.',
      },
      {
        heading: 'How CBD Interacts With THC',
        body: 'CBD can modulate the effects of THC. When present alongside THC, CBD often softens the intensity, reducing anxiety and paranoia while preserving the pleasant aspects of the experience. This is why the THC-to-CBD ratio is one of the most important factors in choosing your experience.',
      },
      {
        heading: 'CBD-Dominant Products',
        body: 'Products with high CBD and low THC offer relaxation without significant psychoactive effects. They\'re ideal for those who want the therapeutic benefits of cannabis — stress relief, pain management, better sleep — without the cerebral intensity.',
      },
      {
        heading: 'The Ratio Matters',
        body: 'A 1:1 THC:CBD ratio produces a balanced, gentle experience. Higher CBD ratios (2:1, 5:1) are progressively more body-focused and clear-headed. Understanding ratios helps you fine-tune every session.',
      },
    ],
  },
  {
    slug: 'the-endocannabinoid-system',
    category: 'basics',
    title: 'The Endocannabinoid System',
    subtitle: 'The system you never learned about in school.',
    readTime: '6 min',
    sections: [
      {
        heading: 'Discovery',
        body: 'The endocannabinoid system (ECS) was discovered in the early 1990s by researchers studying THC. It\'s one of the most important biological systems in the human body, yet it\'s absent from most medical school curricula. The ECS exists in all vertebrates.',
      },
      {
        heading: 'How It Works',
        body: 'The ECS consists of three components: endocannabinoids (naturally produced molecules), receptors (CB1 and CB2, found throughout the body), and enzymes (which break down endocannabinoids after use). This system maintains homeostasis — the body\'s internal balance.',
      },
      {
        heading: 'What It Regulates',
        body: 'The ECS influences sleep, mood, appetite, pain perception, immune function, memory, and reproduction. When your body is stressed, injured, or out of balance, the ECS activates to restore equilibrium.',
      },
      {
        heading: 'Why Cannabis Works',
        body: 'Plant cannabinoids like THC and CBD interact with the same receptors as your body\'s own endocannabinoids. This is why cannabis can influence so many different aspects of your experience — it\'s speaking a language your body already understands.',
      },
    ],
  },
  {
    slug: 'flower-concentrates-edibles',
    category: 'basics',
    title: 'Flower, Concentrates & Edibles',
    subtitle: 'The three pillars of cannabis products.',
    readTime: '5 min',
    sections: [
      {
        heading: 'Flower',
        body: 'The dried buds of the cannabis plant. Flower is the most traditional form and offers the fullest expression of the plant\'s terpene profile. It can be smoked or vaporized and provides rapid onset effects, typically within minutes.',
      },
      {
        heading: 'Concentrates',
        body: 'Concentrated cannabis extracts that isolate specific compounds. Forms include wax, shatter, rosin, and live resin. Concentrates offer higher potency and more pronounced flavor profiles but require careful dosing, especially for newer consumers.',
      },
      {
        heading: 'Edibles',
        body: 'Cannabis-infused food and beverages. Edibles are processed through the digestive system, producing a different, often longer-lasting experience than inhaled cannabis. Onset is slower (30-90 minutes) but effects can last 4-8 hours.',
      },
      {
        heading: 'Choosing Your Format',
        body: 'Each format offers a distinct experience. Flower for authenticity and ritual, concentrates for intensity and purity, edibles for duration and discretion. The best choice depends on the moment you\'re designing.',
      },
    ],
  },
  {
    slug: 'reading-a-label',
    category: 'basics',
    title: 'How to Read a Cannabis Label',
    subtitle: 'Decoding what actually matters.',
    readTime: '4 min',
    sections: [
      {
        heading: 'THC & CBD Percentages',
        body: 'The most prominent numbers on any label. THC percentage indicates psychoactive potency. CBD percentage indicates the presence of the calming counterpart. Remember: higher percentages don\'t automatically mean a better experience.',
      },
      {
        heading: 'Terpene Information',
        body: 'Premium products list their dominant terpenes. This is arguably more useful than THC percentage for predicting your experience. Look for myrcene (relaxing), limonene (uplifting), pinene (alert), or caryophyllene (soothing).',
      },
      {
        heading: 'Testing Information',
        body: 'Lab-tested products display results for potency, pesticides, heavy metals, and microbials. This is non-negotiable for quality. Every Secret 13 product undergoes full-panel testing from independent laboratories.',
      },
      {
        heading: 'Harvest & Package Dates',
        body: 'Freshness matters. Cannabis degrades over time — THC converts to CBN, terpenes evaporate. Look for recent harvest dates and proper packaging (light-proof, airtight). Our collection is always sourced from recent harvests.',
      },
    ],
  },
  {
    slug: 'what-are-cannabinoids',
    category: 'basics',
    title: 'What Are Cannabinoids?',
    subtitle: 'The molecules that shape your experience.',
    readTime: '5 min',
    sections: [
      {
        heading: 'The Cannabinoid Family',
        body: 'Cannabinoids are a class of chemical compounds that interact with the endocannabinoid system. Over 100 different cannabinoids have been identified in the cannabis plant, each with unique properties and potential effects.',
      },
      {
        heading: 'Major Cannabinoids',
        body: 'THC and CBD are the most well-known, but others are gaining attention. CBN (cannabinol) is associated with sedation, CBG (cannabigerol) shows anti-inflammatory properties, and THCV (tetrahydrocannabivarin) may suppress appetite while providing energetic effects.',
      },
      {
        heading: 'Minor Cannabinoids',
        body: 'Compounds like CBC, CBDV, and delta-8 THC exist in smaller quantities but contribute to the overall experience. These minor cannabinoids often work synergistically with major cannabinoids, enhancing or modifying their effects.',
      },
      {
        heading: 'The Entourage Effect',
        body: 'No cannabinoid works in isolation. The entourage effect describes how cannabinoids, terpenes, and flavonoids work together to produce effects greater than any single compound alone. This is why whole-plant products often feel more complete than isolates.',
      },
    ],
  },
  {
    slug: 'cannabis-strains-explained',
    category: 'basics',
    title: 'Cannabis Strains Explained',
    subtitle: 'What a strain name really tells you.',
    readTime: '4 min',
    sections: [
      {
        heading: 'What Is a Strain?',
        body: 'A cannabis strain is a specific variety of the cannabis plant, bred for particular characteristics. Strains have names — like Blue Dream, OG Kush, or Girl Scout Cookies — that reference their lineage, appearance, or effects.',
      },
      {
        heading: 'Genetics & Lineage',
        body: 'Every strain has parent genetics. Understanding lineage can hint at what to expect. Kush-descended strains tend toward relaxation, Haze genetics lean energetic, and Cookies-family strains often offer balanced, dessert-like experiences.',
      },
      {
        heading: 'The Phenotype Problem',
        body: 'The same strain grown by different cultivators can produce vastly different results. Growing conditions, harvest timing, and curing methods all influence the final product. A name alone is never a guarantee of a specific experience.',
      },
      {
        heading: 'Beyond the Name',
        body: 'This is exactly why Secret 13 focuses on curated experiences rather than strain names. We select based on tested results — what the product actually delivers — not what its genetics promise on paper.',
      },
    ],
  },
  {
    slug: 'cannabis-legality-basics',
    category: 'basics',
    title: 'Cannabis Legality',
    subtitle: 'Know your state, know your rights.',
    readTime: '4 min',
    sections: [
      {
        heading: 'A Patchwork of Laws',
        body: 'Cannabis legality varies dramatically by jurisdiction. Some states allow full recreational use, others permit only medical use, and some maintain full prohibition. Federal law in the United States still classifies cannabis as a Schedule I substance, though enforcement priorities have shifted.',
      },
      {
        heading: 'What Legal Means',
        body: 'In legal states, adults 21 and over can purchase cannabis from licensed dispensaries. There are limits on how much you can buy, possess, and where you can consume. Public consumption is typically prohibited even in legal states.',
      },
      {
        heading: 'Travel & Interstate',
        body: 'Cannabis cannot legally cross state lines, even between two legal states. Air travel with cannabis is federally prohibited. Always consume and purchase within the jurisdiction where it\'s legal.',
      },
      {
        heading: 'Stay Informed',
        body: 'Laws are evolving rapidly. What was illegal last year may be legal today, and regulations around testing, packaging, and sales continue to develop. Secret 13 operates exclusively within legal markets and encourages all members to stay informed about their local laws.',
      },
    ],
  },

  // ── THE SCIENCE (8) ──────────────────────────────────────
  {
    slug: 'how-cannabis-affects-the-brain',
    category: 'science',
    title: 'How Cannabis Affects the Brain',
    subtitle: 'Neuroscience, simplified.',
    readTime: '6 min',
    sections: [
      {
        heading: 'The CB1 Receptor',
        body: 'THC primarily activates CB1 receptors, which are densely concentrated in areas of the brain responsible for pleasure, memory, thinking, coordination, and time perception. This explains the wide range of effects cannabis produces.',
      },
      {
        heading: 'Dopamine & Reward',
        body: 'Cannabis triggers dopamine release in the brain\'s reward circuit, similar to other pleasurable experiences like eating good food or listening to music. This creates the sense of euphoria and well-being associated with use.',
      },
      {
        heading: 'Memory & Time',
        body: 'THC affects the hippocampus, the brain region responsible for forming new memories. This is why short-term memory can be impaired during use and why time perception often feels altered — minutes can stretch into what feels like hours.',
      },
      {
        heading: 'The Recovery',
        body: 'These effects are temporary. Once THC is metabolized, normal brain function returns. Long-term cognitive effects are a subject of ongoing research, with most studies suggesting minimal impact in adult users who consume moderately.',
      },
    ],
  },
  {
    slug: 'the-entourage-effect',
    category: 'science',
    title: 'The Entourage Effect',
    subtitle: 'Why the whole is greater than the sum.',
    readTime: '5 min',
    sections: [
      {
        heading: 'The Concept',
        body: 'The entourage effect is the theory that cannabis compounds work better together than in isolation. First proposed by researchers Raphael Mechoulam and Shimon Ben-Shabat in 1998, it suggests that the full spectrum of cannabinoids, terpenes, and flavonoids creates a synergistic effect.',
      },
      {
        heading: 'The Evidence',
        body: 'Studies have shown that CBD can reduce THC-induced anxiety. Terpenes like myrcene may enhance cannabinoid absorption. Limonene has been shown to boost serotonin levels independently. Together, these compounds create experiences that isolates cannot replicate.',
      },
      {
        heading: 'Full Spectrum vs. Isolate',
        body: 'Full-spectrum products contain the complete range of plant compounds and are most likely to produce the entourage effect. Isolates contain only a single compound (like pure CBD). Broad-spectrum products fall between, retaining multiple compounds but removing THC.',
      },
      {
        heading: 'What This Means For You',
        body: 'When choosing a product, the full chemical profile matters more than any single metric. A well-balanced flower with moderate THC and rich terpenes will often outperform a high-THC product with a thin chemical profile.',
      },
    ],
  },
  {
    slug: 'cannabis-and-sleep-science',
    category: 'science',
    title: 'Cannabis & Sleep Science',
    subtitle: 'What research actually tells us.',
    readTime: '5 min',
    sections: [
      {
        heading: 'The Sleep Connection',
        body: 'Cannabis has been used as a sleep aid for centuries. Modern research confirms that certain cannabinoids and terpenes can influence sleep onset, duration, and quality — but the relationship is more complex than simply "it helps you sleep."',
      },
      {
        heading: 'THC & Sleep Architecture',
        body: 'THC reduces the time it takes to fall asleep and may increase total sleep time. However, it also reduces REM sleep — the phase associated with dreaming. This is why heavy users sometimes report vivid dreams when they take a break.',
      },
      {
        heading: 'CBD\'s Role',
        body: 'CBD in moderate doses may promote wakefulness, while higher doses tend to be sedating. The anxiolytic properties of CBD can indirectly improve sleep by reducing the anxiety and racing thoughts that keep people awake.',
      },
      {
        heading: 'The Best Compounds for Sleep',
        body: 'CBN (cannabinol) is the cannabinoid most associated with sedation. The terpenes myrcene and linalool also promote relaxation. Products combining these compounds tend to be the most effective sleep aids in the cannabis space.',
      },
    ],
  },
  {
    slug: 'decarboxylation-explained',
    category: 'science',
    title: 'Decarboxylation',
    subtitle: 'The chemistry that activates cannabis.',
    readTime: '4 min',
    sections: [
      {
        heading: 'What It Is',
        body: 'Raw cannabis doesn\'t contain THC — it contains THCA, the acidic precursor. Decarboxylation is the chemical process that converts THCA into active THC through the application of heat. Without this step, cannabis has no psychoactive effect.',
      },
      {
        heading: 'How Heat Activates',
        body: 'When you smoke or vaporize cannabis, decarboxylation happens instantly. When making edibles, you need to heat cannabis at around 220-245°F for 30-45 minutes. This careful process ensures maximum conversion without destroying valuable terpenes.',
      },
      {
        heading: 'Why Raw Cannabis Is Different',
        body: 'Consuming raw cannabis provides THCA and CBDA — the acidic forms of THC and CBD. These compounds have their own potential benefits (anti-inflammatory, neuroprotective) without any psychoactive effects. This is why raw cannabis juicing has gained interest.',
      },
      {
        heading: 'Temperature Matters',
        body: 'Different compounds activate and degrade at different temperatures. Terpenes are particularly sensitive — many evaporate below 300°F. This is why lower-temperature vaporization often produces a more flavorful, nuanced experience than combustion.',
      },
    ],
  },
  {
    slug: 'tolerance-and-the-brain',
    category: 'science',
    title: 'Tolerance & the Brain',
    subtitle: 'Why the same amount feels different over time.',
    readTime: '5 min',
    sections: [
      {
        heading: 'How Tolerance Develops',
        body: 'With regular cannabis use, CB1 receptors become less responsive to THC. The brain reduces the number and sensitivity of these receptors through a process called downregulation. This means you need more cannabis to achieve the same effect.',
      },
      {
        heading: 'The Timeline',
        body: 'Tolerance can begin developing within days of consistent use. However, it develops at different rates for different effects. Tolerance to the "high" develops faster than tolerance to appetite stimulation or sleep effects.',
      },
      {
        heading: 'The Reset',
        body: 'The good news: tolerance reverses relatively quickly. Research shows that CB1 receptor availability begins returning to normal within 48 hours of abstinence and can fully normalize within 2-4 weeks. This is the science behind "tolerance breaks."',
      },
      {
        heading: 'Managing Your Experience',
        body: 'Rather than continuously increasing dosage, consider cycling your consumption. Periodic breaks — even short ones — keep your experience fresh and your endocannabinoid system responsive. Quality over quantity, always.',
      },
    ],
  },
  {
    slug: 'cannabis-and-pain',
    category: 'science',
    title: 'Cannabis & Pain',
    subtitle: 'How cannabinoids interact with pain signals.',
    readTime: '5 min',
    sections: [
      {
        heading: 'Pain Pathways',
        body: 'Pain is a complex signal processed through multiple pathways in the nervous system. Cannabis interacts with pain at several levels: at the site of injury (through CB2 receptors), in the spinal cord (modulating signal transmission), and in the brain (altering pain perception).',
      },
      {
        heading: 'THC & Pain Relief',
        body: 'THC doesn\'t eliminate pain the way opioids do. Instead, it changes your relationship to pain — making it feel less significant, less distressing. Many patients describe it as "the pain is still there, but it doesn\'t bother me."',
      },
      {
        heading: 'CBD\'s Anti-Inflammatory Role',
        body: 'CBD reduces inflammation by interacting with multiple receptor systems. Chronic inflammation is the root of many pain conditions, making CBD particularly useful for conditions like arthritis, fibromyalgia, and inflammatory bowel disease.',
      },
      {
        heading: 'The Research Landscape',
        body: 'Clinical evidence for cannabis and pain is growing but still developing. What we know: it shows promise for neuropathic pain, cancer-related pain, and chronic conditions where conventional treatments have failed. Always consult a healthcare professional.',
      },
    ],
  },
  {
    slug: 'cannabis-metabolism',
    category: 'science',
    title: 'How Your Body Metabolizes Cannabis',
    subtitle: 'From first inhale to full clearance.',
    readTime: '5 min',
    sections: [
      {
        heading: 'Inhalation Route',
        body: 'When smoked or vaporized, THC passes through the lungs into the bloodstream and reaches the brain within seconds. Peak effects occur within 10-30 minutes. The experience typically lasts 1-3 hours.',
      },
      {
        heading: 'Oral Route',
        body: 'When consumed as an edible, THC is processed by the liver, which converts it to 11-hydroxy-THC — a metabolite that\'s actually more potent and longer-lasting than THC itself. This is why edibles feel different and stronger than inhaled cannabis.',
      },
      {
        heading: 'Individual Variation',
        body: 'Metabolism varies significantly between individuals based on genetics, body composition, liver enzyme activity, and even what you\'ve eaten. Two people consuming identical products can have very different experiences.',
      },
      {
        heading: 'Detection & Clearance',
        body: 'THC is fat-soluble and stored in body fat. While the psychoactive effects last hours, THC metabolites can be detected in the body for days to weeks after use, depending on frequency and sensitivity of the test.',
      },
    ],
  },
  {
    slug: 'genetics-of-cannabis',
    category: 'science',
    title: 'The Genetics of Cannabis',
    subtitle: 'How breeding shapes what you experience.',
    readTime: '5 min',
    sections: [
      {
        heading: 'Selective Breeding',
        body: 'For decades, cultivators have crossbred cannabis plants to emphasize desirable traits: higher potency, specific flavors, particular growth patterns, and targeted effects. This selective breeding has produced the thousands of strains available today.',
      },
      {
        heading: 'Genotype vs. Phenotype',
        body: 'A plant\'s genotype is its genetic blueprint. Its phenotype is how those genes express under specific conditions. The same seed can produce different results depending on light, soil, temperature, and cultivation technique.',
      },
      {
        heading: 'Genetic Testing',
        body: 'Modern genetic testing can map a plant\'s DNA to predict its chemical profile before it even flowers. This technology is revolutionizing cultivation, allowing growers to select for specific terpene and cannabinoid profiles with unprecedented precision.',
      },
      {
        heading: 'The Future of Cannabis Genetics',
        body: 'We\'re entering an era where cannabis can be bred for highly specific experiences — not just "relaxing" or "energizing" but tailored mood profiles, therapeutic targets, and flavor combinations that were impossible a generation ago.',
      },
    ],
  },

  // ── TERPENES & FLAVOR (7) ────────────────────────────────
  {
    slug: 'what-are-terpenes',
    category: 'terpenes',
    title: 'What Are Terpenes?',
    subtitle: 'The aromatic molecules that define the experience.',
    readTime: '5 min',
    sections: [
      {
        heading: 'Beyond Aroma',
        body: 'Terpenes are organic compounds produced by many plants, not just cannabis. They\'re responsible for the distinctive scents of lavender, pine trees, and citrus fruits. In cannabis, terpenes do far more than create aroma — they actively shape the experience.',
      },
      {
        heading: 'How They Work',
        body: 'Terpenes interact with the body through multiple mechanisms. Some bind to cannabinoid receptors, others influence neurotransmitter activity, and many have direct pharmacological effects. Linalool (also found in lavender) is genuinely calming; limonene (citrus) genuinely uplifting.',
      },
      {
        heading: 'The Flavor Architects',
        body: 'When you taste pine, berry, diesel, or tropical fruit in cannabis, you\'re tasting terpenes. Each strain\'s unique terpene profile creates its signature flavor and aroma — and importantly, hints at what the experience will feel like.',
      },
      {
        heading: 'Why They Matter More Than THC',
        body: 'Two strains with identical THC percentages can produce completely different experiences based on their terpene profiles. This is why we at Secret 13 obsess over terpene analysis — it\'s the truest predictor of what a Secret will feel like.',
      },
    ],
  },
  {
    slug: 'myrcene-the-relaxer',
    category: 'terpenes',
    title: 'Myrcene: The Relaxer',
    subtitle: 'The most abundant terpene in cannabis.',
    readTime: '4 min',
    sections: [
      {
        heading: 'Profile',
        body: 'Myrcene is the most common terpene in modern cannabis strains. It has an earthy, musky aroma with hints of fruit and clove. It\'s also found in mangoes, lemongrass, thyme, and hops — which is why some beers have a vaguely cannabis-like aroma.',
      },
      {
        heading: 'Effects',
        body: 'Myrcene is a potent muscle relaxant and sedative. Strains high in myrcene tend to produce the classic "couch-lock" experience — deep physical relaxation, heaviness, and drowsiness. It\'s the terpene most associated with indica-like effects.',
      },
      {
        heading: 'Synergy',
        body: 'Research suggests that myrcene may increase the permeability of the blood-brain barrier, allowing other cannabinoids to take effect more quickly and intensely. This is one reason why myrcene-dominant strains often feel particularly potent.',
      },
      {
        heading: 'Best For',
        body: 'Evening use, sleep preparation, physical recovery, pain relief, and deep relaxation. If your moment calls for stillness and surrender, look for myrcene-dominant profiles.',
      },
    ],
  },
  {
    slug: 'limonene-the-uplifter',
    category: 'terpenes',
    title: 'Limonene: The Uplifter',
    subtitle: 'Citrus sunshine in molecular form.',
    readTime: '4 min',
    sections: [
      {
        heading: 'Profile',
        body: 'Limonene is the second most common terpene in nature. It gives citrus fruits their characteristic smell — oranges, lemons, limes, grapefruits. In cannabis, limonene creates bright, zesty, energetic aromas.',
      },
      {
        heading: 'Effects',
        body: 'Limonene is associated with elevated mood, stress relief, and anti-anxiety effects. It may increase serotonin and dopamine levels in the brain, which explains its mood-enhancing properties. Strains high in limonene tend to feel uplifting and clear-headed.',
      },
      {
        heading: 'Beyond Cannabis',
        body: 'Limonene has been studied for potential anti-cancer, anti-fungal, and anti-bacterial properties. It\'s widely used in aromatherapy for stress relief and is FDA-approved as a food additive due to its excellent safety profile.',
      },
      {
        heading: 'Best For',
        body: 'Daytime use, social situations, creative work, mood enhancement, and combating stress. When you need energy without edge, limonene is your terpene.',
      },
    ],
  },
  {
    slug: 'pinene-the-clarifier',
    category: 'terpenes',
    title: 'Pinene: The Clarifier',
    subtitle: 'Forest air, bottled.',
    readTime: '4 min',
    sections: [
      {
        heading: 'Profile',
        body: 'Alpha-pinene is the most widely encountered terpene in nature — it\'s what makes a pine forest smell like a pine forest. In cannabis, pinene creates sharp, clean, woody aromas often described as "fresh" or "crisp."',
      },
      {
        heading: 'Effects',
        body: 'Pinene is unique among terpenes for its potential to counteract THC-induced short-term memory impairment. It\'s a bronchodilator (opens airways), promotes alertness, and may enhance focus and retention.',
      },
      {
        heading: 'The Anti-Fog Terpene',
        body: 'If you\'ve ever found that certain strains keep you sharp and clear-headed despite their THC content, pinene may be the reason. It works to maintain cognitive function while allowing the relaxation and mood benefits of other compounds.',
      },
      {
        heading: 'Best For',
        body: 'Working sessions, studying, outdoor activities, conversations that require presence, and anytime you want the benefits of cannabis without mental cloudiness.',
      },
    ],
  },
  {
    slug: 'caryophyllene-the-soother',
    category: 'terpenes',
    title: 'Caryophyllene: The Soother',
    subtitle: 'The only terpene that acts like a cannabinoid.',
    readTime: '4 min',
    sections: [
      {
        heading: 'Profile',
        body: 'Beta-caryophyllene has a warm, spicy, peppery aroma. It\'s found in black pepper, cloves, cinnamon, and oregano. It\'s one of the most therapeutically interesting terpenes due to a unique property no other terpene shares.',
      },
      {
        heading: 'The CB2 Connection',
        body: 'Caryophyllene is the only known terpene that directly activates CB2 cannabinoid receptors. This makes it functionally a dietary cannabinoid — it produces anti-inflammatory and analgesic effects through the same system that CBD targets.',
      },
      {
        heading: 'Effects',
        body: 'Anti-inflammatory, analgesic, anti-anxiety, and gastroprotective. Caryophyllene can reduce inflammation, ease physical discomfort, and provide a grounding, calming body sensation without any sedation.',
      },
      {
        heading: 'Best For',
        body: 'Physical recovery, stress relief, evening unwinding, and situations where you want body comfort without heaviness. It pairs beautifully with myrcene for deep relaxation or with limonene for alert calm.',
      },
    ],
  },
  {
    slug: 'linalool-the-tranquilizer',
    category: 'terpenes',
    title: 'Linalool: The Tranquilizer',
    subtitle: 'Lavender\'s gift to cannabis.',
    readTime: '4 min',
    sections: [
      {
        heading: 'Profile',
        body: 'Linalool is the dominant terpene in lavender and is responsible for its universally recognized calming scent. In cannabis, linalool adds floral, slightly sweet notes and is often found in strains known for relaxation and stress relief.',
      },
      {
        heading: 'Effects',
        body: 'Linalool is a powerful anxiolytic and sedative. It enhances the calming effects of other compounds and has been shown to reduce aggressive behavior, improve sleep quality, and provide significant anti-anxiety benefits in clinical studies.',
      },
      {
        heading: 'Therapeutic Potential',
        body: 'Beyond its calming effects, linalool has demonstrated anticonvulsant, anti-inflammatory, and local anesthetic properties. It may also provide neuroprotective benefits, with some research suggesting it could help reduce brain inflammation.',
      },
      {
        heading: 'Best For',
        body: 'Wind-down rituals, anxiety management, pre-sleep preparation, and any moment that calls for deep, genuine calm. Linalool-dominant strains are some of the most therapeutically valuable in the cannabis world.',
      },
    ],
  },
  {
    slug: 'terpene-profiles-and-pairing',
    category: 'terpenes',
    title: 'Terpene Profiles & Pairing',
    subtitle: 'How to match terpenes to your moment.',
    readTime: '5 min',
    sections: [
      {
        heading: 'Reading a Terpene Profile',
        body: 'A terpene profile lists the dominant terpenes in a product and their concentrations. The top three terpenes typically determine the character of the experience. Think of it like reading wine tasting notes — it tells you what to expect.',
      },
      {
        heading: 'Pairing With Mood',
        body: 'Want energy? Look for limonene + pinene. Seeking deep relaxation? Myrcene + linalool. Need focus with calm? Pinene + caryophyllene. Creative flow? Terpinolene + limonene. Match the terpene profile to the experience you\'re designing.',
      },
      {
        heading: 'Pairing With Activities',
        body: 'Outdoor adventure: pinene-dominant. Dinner party: limonene + caryophyllene. Movie night: myrcene + linalool. Morning creativity: terpinolene + pinene. The right pairing elevates the moment from ordinary to intentional.',
      },
      {
        heading: 'Building Your Palate',
        body: 'Like wine or coffee, developing a terpene palate takes time and attention. Start noting which terpenes you enjoy, which effects you prefer, and how different profiles make you feel. Over time, you\'ll be able to predict your experience before it begins.',
      },
    ],
  },

  // ── CONSUMPTION METHODS (7) ──────────────────────────────
  {
    slug: 'smoking-the-original',
    category: 'methods',
    title: 'Smoking: The Original',
    subtitle: 'The most ancient consumption method.',
    readTime: '4 min',
    sections: [
      {
        heading: 'The Experience',
        body: 'Smoking cannabis — through a joint, pipe, or bong — provides the fastest onset and most controllable dosing of any method. Effects begin within seconds and peak within minutes, making it easy to find your ideal level.',
      },
      {
        heading: 'The Tradeoffs',
        body: 'Combustion occurs above 600°F, which destroys many delicate terpenes and creates byproducts that are harsh on the lungs. While cannabis smoke is less harmful than tobacco smoke, it still contains irritants that can affect respiratory health.',
      },
      {
        heading: 'Optimizing the Experience',
        body: 'Use a grinder for even burn. Choose unbleached, natural papers. Light with hemp wick instead of butane lighters to avoid inhaling lighter fluid. Take smaller, measured draws rather than deep pulls.',
      },
      {
        heading: 'When to Choose This Method',
        body: 'Smoking offers ritual, tradition, and social connection. The act of rolling, lighting, and sharing has cultural significance that transcends the plant itself. It\'s best for those who value the ceremony as much as the outcome.',
      },
    ],
  },
  {
    slug: 'vaporizing-the-modern-way',
    category: 'methods',
    title: 'Vaporizing: The Modern Way',
    subtitle: 'Maximum flavor, minimal compromise.',
    readTime: '5 min',
    sections: [
      {
        heading: 'How It Works',
        body: 'Vaporizers heat cannabis to a temperature that releases cannabinoids and terpenes as vapor without reaching combustion. Most operate between 350-420°F — hot enough to activate compounds but cool enough to preserve the plant\'s full flavor profile.',
      },
      {
        heading: 'The Advantages',
        body: 'Vaporizing produces significantly fewer harmful byproducts than smoking, preserves more terpenes (resulting in better flavor), and is generally more efficient — extracting more active compounds from the same amount of flower.',
      },
      {
        heading: 'Temperature Control',
        body: 'Different temperatures unlock different compounds. 350°F emphasizes terpenes and light cannabinoids. 380°F provides a balanced experience. 410°F extracts maximum THC but sacrifices some terpenes. Experiment to find your sweet spot.',
      },
      {
        heading: 'Choosing a Vaporizer',
        body: 'Desktop vaporizers offer superior vapor quality. Portable vaporizers provide convenience. Look for precise temperature control, quality vapor path materials (ceramic or glass), and reputable brands with transparent lab testing.',
      },
    ],
  },
  {
    slug: 'edibles-the-long-game',
    category: 'methods',
    title: 'Edibles: The Long Game',
    subtitle: 'Patience rewarded.',
    readTime: '5 min',
    sections: [
      {
        heading: 'A Different Experience',
        body: 'Edibles produce a fundamentally different experience than inhaled cannabis. THC processed through the liver converts to 11-hydroxy-THC, which is more potent, longer-lasting, and produces a more full-body effect.',
      },
      {
        heading: 'The Golden Rule',
        body: 'Start low, go slow. A standard dose is 5-10mg of THC. New consumers should start with 2.5-5mg and wait at least 2 hours before considering more. The most common edible mistake is impatience — taking a second dose before the first has fully manifested.',
      },
      {
        heading: 'Onset & Duration',
        body: 'Effects typically begin 30-90 minutes after consumption, depending on metabolism and stomach contents. Peak effects occur at 2-3 hours. The total experience can last 4-8 hours. Eating on an empty stomach speeds onset; eating with fat-rich food may enhance absorption.',
      },
      {
        heading: 'Types of Edibles',
        body: 'Gummies and chocolates are most common, but the category includes beverages, mints, capsules, and infused foods. Nano-emulsified edibles use technology to reduce onset time to 15-30 minutes, bridging the gap between inhaled and traditional edible experiences.',
      },
    ],
  },
  {
    slug: 'tinctures-and-sublinguals',
    category: 'methods',
    title: 'Tinctures & Sublinguals',
    subtitle: 'Precision in a dropper.',
    readTime: '4 min',
    sections: [
      {
        heading: 'What They Are',
        body: 'Tinctures are liquid cannabis extracts, typically in an alcohol or oil base. Applied under the tongue (sublingual), they absorb through the mucous membranes directly into the bloodstream, bypassing the digestive system.',
      },
      {
        heading: 'The Precision Advantage',
        body: 'Tinctures offer the most precise dosing of any consumption method. Measured in milligrams per dropper, you can dial in your exact dose with repeatability. This makes them ideal for therapeutic use or for those who want consistent, predictable experiences.',
      },
      {
        heading: 'Onset & Duration',
        body: 'Sublingual absorption produces effects within 15-30 minutes — faster than edibles but slower than inhalation. Effects typically last 2-4 hours. If swallowed rather than held under the tongue, the tincture behaves more like an edible.',
      },
      {
        heading: 'Discretion & Convenience',
        body: 'No smoke, no vapor, no odor. Tinctures are the most discreet consumption method. A small bottle fits in a pocket, and a dose takes seconds to administer. They\'re odorless and can be added to food or beverages.',
      },
    ],
  },
  {
    slug: 'topicals-external-relief',
    category: 'methods',
    title: 'Topicals: External Relief',
    subtitle: 'Cannabis that never enters your bloodstream.',
    readTime: '4 min',
    sections: [
      {
        heading: 'How They Work',
        body: 'Cannabis topicals — creams, balms, lotions, and patches — are applied directly to the skin. Cannabinoids bind to local CB2 receptors in the skin and underlying tissue, providing targeted relief without entering the bloodstream or producing psychoactive effects.',
      },
      {
        heading: 'No High, Real Benefits',
        body: 'Because topicals don\'t reach the bloodstream (with the exception of transdermal patches), they won\'t produce a "high." This makes them accessible to anyone seeking the anti-inflammatory and analgesic benefits of cannabis without any psychoactive experience.',
      },
      {
        heading: 'Applications',
        body: 'Joint and muscle pain, skin conditions (eczema, psoriasis), localized inflammation, headaches (temple application), and post-workout recovery. Some users report relief from menstrual cramps and arthritis with regular topical application.',
      },
      {
        heading: 'Transdermal Patches',
        body: 'Unlike regular topicals, transdermal patches are designed to penetrate through the skin into the bloodstream. They provide steady, sustained cannabinoid delivery over several hours and can produce systemic effects including mild psychoactivity.',
      },
    ],
  },
  {
    slug: 'dabbing-and-concentrates',
    category: 'methods',
    title: 'Dabbing & Concentrates',
    subtitle: 'For experienced palates only.',
    readTime: '5 min',
    sections: [
      {
        heading: 'What Is Dabbing?',
        body: 'Dabbing involves vaporizing concentrated cannabis extracts on a heated surface and inhaling the resulting vapor. It\'s the most potent and flavorful consumption method, delivering high doses of cannabinoids and terpenes in a single inhalation.',
      },
      {
        heading: 'Types of Concentrates',
        body: 'Shatter (glass-like), wax (soft), budder (creamy), live resin (terpene-rich), rosin (solventless), and diamonds (crystalline THCA). Each type has a different texture, flavor, and potency profile. Live resin and rosin are considered the most premium.',
      },
      {
        heading: 'The Intensity Factor',
        body: 'Concentrates typically range from 60-90% THC, compared to 15-30% for flower. This means a single dab delivers far more THC than a typical bowl. This is not a method for beginners — start with tiny amounts and work up.',
      },
      {
        heading: 'Temperature & Technique',
        body: 'Low-temperature dabs (400-500°F) preserve terpenes and produce smooth, flavorful vapor. High-temperature dabs produce thicker vapor but sacrifice flavor. A quality dab rig, electronic nail, or portable dab pen makes a significant difference in the experience.',
      },
    ],
  },
  {
    slug: 'micro-dosing',
    category: 'methods',
    title: 'Micro-Dosing',
    subtitle: 'The art of less.',
    readTime: '4 min',
    sections: [
      {
        heading: 'The Philosophy',
        body: 'Micro-dosing is the practice of consuming very small amounts of cannabis — typically 1-5mg of THC — to achieve subtle, functional effects without full psychoactive intensity. It\'s about enhancement, not alteration.',
      },
      {
        heading: 'What It Feels Like',
        body: 'A successful micro-dose often doesn\'t feel like much at all. Subtle mood lift, slight stress reduction, enhanced sensory appreciation, marginal creativity boost. If you feel "high," you\'ve taken too much for a micro-dose.',
      },
      {
        heading: 'Methods for Micro-Dosing',
        body: 'Tinctures offer the most precise dosing. Low-dose edibles (2.5-5mg) are convenient. One-hitter pipes provide small, measured inhalations. Some vaporizers have single-draw modes designed for micro-dosing.',
      },
      {
        heading: 'Who It\'s For',
        body: 'Professionals seeking subtle stress relief during the workday, creatives wanting a gentle boost, medical patients who need symptom management without impairment, and anyone who wants to integrate cannabis into daily life without it becoming the focus.',
      },
    ],
  },

  // ── WELLNESS & EFFECTS (8) ───────────────────────────────
  {
    slug: 'cannabis-and-anxiety',
    category: 'wellness',
    title: 'Cannabis & Anxiety',
    subtitle: 'A nuanced relationship.',
    readTime: '5 min',
    sections: [
      {
        heading: 'The Dual Nature',
        body: 'Cannabis can both reduce and increase anxiety, depending on the dose, chemical profile, individual biology, and setting. Low doses of THC tend to be anxiolytic (anxiety-reducing). Higher doses can trigger or amplify anxiety, especially in sensitive individuals.',
      },
      {
        heading: 'The CBD Factor',
        body: 'CBD is a reliable anxiolytic at most doses. Products with balanced THC:CBD ratios or CBD-dominant profiles are generally better choices for anxiety-prone individuals. CBD can counteract THC-induced anxiety when both are present.',
      },
      {
        heading: 'Set & Setting',
        body: 'Your environment and mindset dramatically influence the experience. Cannabis amplifies your current emotional state. If you\'re already anxious, a stimulating environment plus high-THC cannabis is a recipe for discomfort. Choose calming settings and strains.',
      },
      {
        heading: 'Finding Your Zone',
        body: 'Start with low-THC, high-CBD products. Use in comfortable, familiar settings. Avoid when highly stressed or in unfamiliar environments. Keep black pepper nearby — caryophyllene can help counteract THC-induced anxiety in a pinch.',
      },
    ],
  },
  {
    slug: 'cannabis-and-creativity',
    category: 'wellness',
    title: 'Cannabis & Creativity',
    subtitle: 'The muse in the molecule.',
    readTime: '5 min',
    sections: [
      {
        heading: 'The Creative Connection',
        body: 'Many artists, musicians, and thinkers have reported that cannabis enhances their creative process. Research suggests this may be due to increased divergent thinking — the ability to generate many different ideas from a single starting point.',
      },
      {
        heading: 'How It Works',
        body: 'THC increases blood flow to the frontal lobe, the brain region associated with creative thinking and abstract reasoning. It also loosens associative thinking, allowing your mind to make connections it might normally filter out.',
      },
      {
        heading: 'The Dose Curve',
        body: 'Creativity and cannabis follow an inverted-U curve. Low to moderate doses enhance creative thinking. High doses tend to impair the executive function needed to actually execute creative ideas. The sweet spot is just enough to loosen thinking without losing coherence.',
      },
      {
        heading: 'Optimizing for Creative Work',
        body: 'Use sativa-leaning profiles with limonene and pinene. Start with a low dose. Have your tools ready before consumption — the creative window opens quickly and you want to capture ideas immediately. Journal, sketch, or record voice notes.',
      },
    ],
  },
  {
    slug: 'cannabis-and-exercise',
    category: 'wellness',
    title: 'Cannabis & Exercise',
    subtitle: 'The runner\'s not-so-secret secret.',
    readTime: '4 min',
    sections: [
      {
        heading: 'The Trend',
        body: 'A growing number of athletes and fitness enthusiasts are incorporating cannabis into their routines — for pre-workout motivation, in-session flow states, and post-workout recovery. It\'s more common than you might think.',
      },
      {
        heading: 'Pre-Workout',
        body: 'Low doses of sativa-leaning strains can increase motivation, improve mind-muscle connection, and make repetitive exercise more engaging. Some runners report that cannabis helps them achieve a flow state more easily. Micro-dosing is key here.',
      },
      {
        heading: 'Post-Workout',
        body: 'Cannabis shines as a recovery tool. CBD reduces inflammation and muscle soreness. THC helps with pain management. Topicals applied directly to sore muscles provide targeted relief. Many athletes prefer cannabis-based recovery over NSAIDs.',
      },
      {
        heading: 'Important Caveats',
        body: 'Cannabis impairs coordination and reaction time. It\'s not appropriate for activities requiring sharp reflexes or precise balance. Never use before driving or operating equipment. Stick to low doses and familiar activities.',
      },
    ],
  },
  {
    slug: 'cannabis-and-meditation',
    category: 'wellness',
    title: 'Cannabis & Meditation',
    subtitle: 'Two ancient practices, one intention.',
    readTime: '4 min',
    sections: [
      {
        heading: 'A Shared History',
        body: 'Cannabis has been used as a meditation aid for millennia, from ancient Hindu sadhus to Rastafarian spiritual practice. Both cannabis and meditation aim to alter consciousness, reduce mental chatter, and create space for presence.',
      },
      {
        heading: 'How Cannabis Can Help',
        body: 'For those who struggle to quiet their minds, a small dose of cannabis can reduce the volume of racing thoughts, making it easier to access meditative states. It can heighten body awareness and deepen the connection between breath and sensation.',
      },
      {
        heading: 'Choosing Your Aid',
        body: 'Indica-leaning strains with linalool and myrcene support stillness meditation. Balanced hybrids with limonene suit walking meditation or mindful movement. CBD-dominant products offer calm without cognitive alteration.',
      },
      {
        heading: 'The Practice',
        body: 'Consume 15-20 minutes before sitting. Use a low dose. Focus on breath. When the mind wanders, gently return — cannabis can make this return feel more natural and less frustrating. End with gratitude for the moment you\'ve created.',
      },
    ],
  },
  {
    slug: 'managing-overconsumption',
    category: 'wellness',
    title: 'Managing Overconsumption',
    subtitle: 'What to do when you\'ve had too much.',
    readTime: '4 min',
    sections: [
      {
        heading: 'It Happens',
        body: 'Even experienced consumers occasionally overshoot their comfort zone. The most important thing to know: cannabis overconsumption is uncomfortable but not dangerous. No one has ever fatally overdosed on cannabis alone.',
      },
      {
        heading: 'Immediate Steps',
        body: 'Find a calm, safe space. Drink water. Take slow, deep breaths. Chew black peppercorns — the caryophyllene can counteract THC anxiety. If available, take CBD, which can reduce THC\'s psychoactive intensity. Lie down if needed.',
      },
      {
        heading: 'What to Expect',
        body: 'Symptoms may include anxiety, paranoia, rapid heartbeat, dizziness, nausea, and disorientation. These will pass. For inhaled cannabis, discomfort typically subsides within 30-90 minutes. For edibles, it may take several hours.',
      },
      {
        heading: 'Prevention',
        body: 'Know your dose. Start low with new products. Wait before re-dosing. Be cautious with edibles. Avoid mixing with alcohol. Consume in comfortable environments with trusted people. Being intentional about dosing prevents almost all negative experiences.',
      },
    ],
  },
  {
    slug: 'cannabis-and-nutrition',
    category: 'wellness',
    title: 'Cannabis & Nutrition',
    subtitle: 'The munchies, decoded.',
    readTime: '4 min',
    sections: [
      {
        heading: 'Why It Triggers Hunger',
        body: 'THC stimulates appetite through multiple mechanisms: it activates CB1 receptors in the hypothalamus (the brain\'s hunger center), increases the release of the hunger hormone ghrelin, and enhances the sensitivity of smell and taste receptors.',
      },
      {
        heading: 'Strategic Eating',
        body: 'Instead of fighting the munchies, plan for them. Prepare healthy, satisfying snacks before your session. Fresh fruit, nuts, dark chocolate, and cheese are popular choices that satisfy without the regret of processed junk food.',
      },
      {
        heading: 'Cannabis & Metabolism',
        body: 'Interestingly, regular cannabis users tend to have lower BMIs and better insulin sensitivity than non-users, despite increased caloric intake. Researchers believe this may be due to THCV and other compounds that influence metabolic function.',
      },
      {
        heading: 'Mindful Consumption',
        body: 'Cannabis can make eating a more sensory, enjoyable experience. Use this to your advantage: cook a beautiful meal, eat slowly, savor flavors you might normally overlook. Turn the munchies from a pitfall into a pleasure.',
      },
    ],
  },
  {
    slug: 'tolerance-breaks',
    category: 'wellness',
    title: 'Tolerance Breaks',
    subtitle: 'The reset that refreshes everything.',
    readTime: '4 min',
    sections: [
      {
        heading: 'Why Take a Break',
        body: 'Regular cannabis use leads to tolerance — needing more for the same effect. A tolerance break (T-break) allows your endocannabinoid system to reset, making subsequent experiences more potent, more flavorful, and more enjoyable.',
      },
      {
        heading: 'The Science of Reset',
        body: 'CB1 receptor density begins recovering within 48 hours of abstinence. Significant recovery occurs within one week. Full normalization typically takes 2-4 weeks. Even a short 3-day break can noticeably refresh your experience.',
      },
      {
        heading: 'What to Expect',
        body: 'The first few days may bring mild irritability, sleep disruption, vivid dreams, decreased appetite, and restlessness. These are normal and temporary — signs that your endocannabinoid system is recalibrating. They typically resolve within a week.',
      },
      {
        heading: 'Making It Count',
        body: 'Use the break to reflect on your relationship with cannabis. Are you consuming intentionally or habitually? When you return, start with a lower dose than your pre-break amount. You\'ll likely be pleasantly surprised by how little you need.',
      },
    ],
  },
  {
    slug: 'cannabis-and-social-connection',
    category: 'wellness',
    title: 'Cannabis & Social Connection',
    subtitle: 'Shared moments, amplified.',
    readTime: '4 min',
    sections: [
      {
        heading: 'The Social Catalyst',
        body: 'Cannabis has been a social lubricant across cultures for millennia. It can reduce social anxiety, enhance empathy, promote laughter, and create a shared sense of presence that deepens interpersonal connection.',
      },
      {
        heading: 'Choosing for Social Situations',
        body: 'Sativa-leaning strains with limonene and terpinolene tend to promote talkativeness and energy. Avoid heavy indicas that might make you withdraw. Moderate doses keep you engaged — overconsumption can lead to introspection rather than interaction.',
      },
      {
        heading: 'The Shared Ritual',
        body: 'Passing a joint or sharing an edible experience creates a bond. There\'s an intimacy to the act of sharing something that alters consciousness together. It signals trust and creates a shared vulnerability that can deepen relationships.',
      },
      {
        heading: 'Respecting Boundaries',
        body: 'Never pressure anyone to consume. Always ask before lighting up in shared spaces. Be mindful of varying tolerance levels in a group. The best social cannabis experiences happen when everyone feels comfortable and in control.',
      },
    ],
  },

  // ── LIFESTYLE & CULTURE (5) ──────────────────────────────
  {
    slug: 'the-history-of-cannabis',
    category: 'lifestyle',
    title: 'The History of Cannabis',
    subtitle: '12,000 years in five minutes.',
    readTime: '6 min',
    sections: [
      {
        heading: 'Ancient Origins',
        body: 'Cannabis is among the oldest cultivated plants. Archaeological evidence from China dates hemp cultivation to 10,000 BCE. By 2,700 BCE, Chinese Emperor Shen Nung was documenting its medicinal uses. The plant spread along trade routes to India, Africa, and eventually Europe.',
      },
      {
        heading: 'Sacred Plant',
        body: 'Cannabis held spiritual significance in many ancient cultures. Hindu texts reference it as one of five sacred plants. Scythian burial sites contain cannabis burning implements. Ancient Egyptians used it medicinally. It was woven into the spiritual and medicinal fabric of societies worldwide.',
      },
      {
        heading: 'Prohibition',
        body: 'The 20th century brought dramatic change. The Marihuana Tax Act of 1937, the War on Drugs in the 1970s, and decades of criminalization — disproportionately affecting communities of color — created a stigma that persists today. The science never supported these policies.',
      },
      {
        heading: 'The Renaissance',
        body: 'Starting with California\'s medical legalization in 1996, a wave of reform has swept across the globe. Today, cannabis is legal for adult use in growing number of jurisdictions, and the industry has evolved from underground to ultra-premium. We are living in a cannabis renaissance.',
      },
    ],
  },
  {
    slug: 'cannabis-and-music',
    category: 'lifestyle',
    title: 'Cannabis & Music',
    subtitle: 'Why everything sounds better.',
    readTime: '4 min',
    sections: [
      {
        heading: 'The Enhancement Effect',
        body: 'Cannabis genuinely enhances music appreciation. THC affects auditory processing in the brain, increasing attention to sonic detail, expanding the perception of stereo space, and heightening emotional response to melody and rhythm.',
      },
      {
        heading: 'The Science',
        body: 'Studies show that cannabis users demonstrate increased neural activity in the auditory cortex during music listening. THC also disrupts short-term memory in a way that makes each musical moment feel fresh — you\'re hearing parts of the song as if for the first time.',
      },
      {
        heading: 'Pairing Guide',
        body: 'Sativa + electronic music = heightened rhythm and energy. Indica + ambient = immersive soundscapes. Balanced hybrid + jazz = appreciation of improvisation. CBD-dominant + classical = deep focus on compositional structure.',
      },
      {
        heading: 'The Ritual',
        body: 'Invest in good headphones or speakers. Choose an album, not a playlist. Consume, then lie down, close your eyes, and let the music surround you. This is one of the most consistently rewarding cannabis experiences available.',
      },
    ],
  },
  {
    slug: 'cannabis-and-food-pairing',
    category: 'lifestyle',
    title: 'Cannabis & Food Pairing',
    subtitle: 'Terpene meets cuisine.',
    readTime: '5 min',
    sections: [
      {
        heading: 'The New Sommelier',
        body: 'Just as wine is paired with food based on flavor compounds, cannabis can be paired based on its terpene profile. Matching shared terpenes between your food and your flower creates harmonious, elevated dining experiences.',
      },
      {
        heading: 'Pairing Principles',
        body: 'Limonene-dominant strains pair with citrus dishes, seafood, and light salads. Myrcene-heavy profiles complement rich, herbal dishes like roasted lamb or mushroom risotto. Caryophyllene-rich strains enhance spicy cuisine — Thai, Indian, Mexican.',
      },
      {
        heading: 'The Enhanced Palate',
        body: 'Cannabis heightens taste and smell sensitivity. Flavors become more vivid, textures more pronounced. Use this enhancement intentionally: prepare quality ingredients, eat slowly, and notice subtleties you might normally miss.',
      },
      {
        heading: 'Hosting a Pairing Dinner',
        body: 'Select 3-4 courses, each paired with a different strain or product. Provide small amounts — the goal is tasting, not heavy consumption. Include palate cleansers between courses. Educate your guests on what to notice. It\'s wine dinner energy, elevated.',
      },
    ],
  },
  {
    slug: 'cannabis-etiquette',
    category: 'lifestyle',
    title: 'Cannabis Etiquette',
    subtitle: 'The unwritten rules, written.',
    readTime: '4 min',
    sections: [
      {
        heading: 'Sharing',
        body: 'If someone shares with you, reciprocate when you can. Don\'t pocket the lighter. Corner the bowl (light only a portion) so everyone gets green hits. Pass to the left. These small courtesies are the social fabric of cannabis culture.',
      },
      {
        heading: 'Consent & Respect',
        body: 'Never pressure anyone to consume. Ask before smoking in someone\'s space. Be mindful of secondhand exposure around non-consumers and especially around children or pets. Respect "no" without requiring an explanation.',
      },
      {
        heading: 'Dosage Awareness',
        body: 'When sharing edibles, always disclose the dosage. When in a group with mixed experience levels, offer lower-potency options first. Check in on newcomers throughout the experience. Being the experienced friend who looks out for others is the highest etiquette.',
      },
      {
        heading: 'Public Spaces',
        body: 'Even in legal jurisdictions, be discreet. Don\'t consume in parks, sidewalks, or places where others might be affected. Use odor-reducing methods in shared living spaces. Being a considerate consumer benefits the entire community.',
      },
    ],
  },
  {
    slug: 'building-your-collection',
    category: 'lifestyle',
    title: 'Building Your Collection',
    subtitle: 'Curating experiences, not products.',
    readTime: '4 min',
    sections: [
      {
        heading: 'The Collector\'s Mindset',
        body: 'A thoughtful cannabis collection isn\'t about having the most — it\'s about having the right options for any moment. Think of it as a personal bar: you don\'t need fifty bottles, but you want something perfect for every occasion.',
      },
      {
        heading: 'The Essential Three',
        body: 'Start with three anchors: one for energy and creativity (sativa-leaning, pinene/limonene), one for relaxation and sleep (indica-leaning, myrcene/linalool), and one balanced option for social situations and everyday enhancement.',
      },
      {
        heading: 'Storage',
        body: 'Cannabis degrades with exposure to light, air, heat, and moisture. Store in airtight glass containers in a cool, dark place. Use humidity packs (62% RH) to maintain optimal moisture. Properly stored flower can retain quality for months.',
      },
      {
        heading: 'The Secret 13 Advantage',
        body: 'Our collection is designed with this philosophy in mind. Each Secret occupies a distinct space in the experience spectrum. A complete Secret 13 collection gives you the range to match any moment, any mood, any company.',
      },
    ],
  },

  // ── SAFETY & RESPONSIBILITY (5) ──────────────────────────
  {
    slug: 'responsible-consumption',
    category: 'safety',
    title: 'Responsible Consumption',
    subtitle: 'Enjoy fully. Consume wisely.',
    readTime: '4 min',
    sections: [
      {
        heading: 'Know Your Limits',
        body: 'Cannabis affects everyone differently. Your ideal dose depends on your tolerance, body chemistry, metabolism, and the specific product. There is no universal "right amount" — only what\'s right for you, in this moment.',
      },
      {
        heading: 'Start Low, Go Slow',
        body: 'Especially with new products or methods, begin with the minimum effective dose. You can always consume more, but you can\'t unconsume what you\'ve taken. Patience is the most important skill in cannabis consumption.',
      },
      {
        heading: 'Plan Ahead',
        body: 'Don\'t drive. Have food and water accessible. Be in a comfortable environment. Tell someone you trust if you\'re trying something new. Have CBD available as a moderating tool. Simple preparation prevents most negative experiences.',
      },
      {
        heading: 'Intentional, Not Habitual',
        body: 'The best cannabis experiences are intentional ones. Ask yourself: why am I consuming right now? Is it to enhance a moment, or to escape one? Cannabis serves you best when it\'s a conscious choice, not an automatic habit.',
      },
    ],
  },
  {
    slug: 'cannabis-and-driving',
    category: 'safety',
    title: 'Cannabis & Driving',
    subtitle: 'The one rule with no exceptions.',
    readTime: '3 min',
    sections: [
      {
        heading: 'Never Drive Impaired',
        body: 'Cannabis impairs reaction time, lane tracking, divided attention, and decision-making. These effects can persist for hours after consumption. There is no safe amount of cannabis for driving. Period.',
      },
      {
        heading: 'The Legal Reality',
        body: 'DUI laws apply to cannabis in every jurisdiction. Penalties include license suspension, fines, and criminal charges. Some states use blood THC thresholds; others rely on officer observation. A DUI conviction affects your record for years.',
      },
      {
        heading: 'Plan Your Transportation',
        body: 'Arrange a ride before you consume. Use rideshare services. Stay where you are until you\'re fully sober. If hosting, ensure your guests have safe rides home. This is the baseline of responsible consumption — non-negotiable.',
      },
      {
        heading: 'The Bigger Picture',
        body: 'Every impaired driving incident reflects on the entire cannabis community. As legalization expands, demonstrating that cannabis consumers are responsible helps advance the cause and protects the freedoms we\'ve gained.',
      },
    ],
  },
  {
    slug: 'cannabis-and-mental-health',
    category: 'safety',
    title: 'Cannabis & Mental Health',
    subtitle: 'What to know. What to watch.',
    readTime: '5 min',
    sections: [
      {
        heading: 'The Complexity',
        body: 'Cannabis and mental health have a complicated relationship. For many people, cannabis provides genuine relief from anxiety, depression, PTSD, and stress. For others — particularly those with certain predispositions — it can exacerbate or trigger mental health challenges.',
      },
      {
        heading: 'Risk Factors',
        body: 'Individuals with a personal or family history of psychosis, schizophrenia, or bipolar disorder should exercise particular caution. High-THC products consumed frequently during adolescence have been associated with increased risk in predisposed individuals.',
      },
      {
        heading: 'Signs to Watch',
        body: 'If cannabis consistently increases your anxiety rather than reducing it, if you find yourself unable to function without it, if it\'s replacing professional mental health care, or if loved ones express concern — these are signals to reassess your relationship with it.',
      },
      {
        heading: 'The Balanced Approach',
        body: 'Cannabis can be part of a healthy mental wellness routine, but it shouldn\'t be the entire routine. Combine with exercise, therapy, social connection, and good sleep hygiene. If you\'re using cannabis to manage a diagnosed condition, involve your healthcare provider.',
      },
    ],
  },
  {
    slug: 'safe-storage',
    category: 'safety',
    title: 'Safe Storage',
    subtitle: 'Keep it safe. Keep it yours.',
    readTime: '3 min',
    sections: [
      {
        heading: 'Away From Children',
        body: 'Cannabis products — especially edibles that resemble candy or treats — must be stored completely out of reach of children. Use lockable containers or storage spaces. Cannabis-related emergency room visits among children have increased with legalization. This is preventable.',
      },
      {
        heading: 'Away From Pets',
        body: 'Cannabis is toxic to dogs and cats. THC affects them more intensely than humans, and edibles pose the greatest risk. Store all products in pet-proof containers. If a pet ingests cannabis, contact a veterinarian immediately.',
      },
      {
        heading: 'Proper Conditions',
        body: 'Store in airtight, opaque containers at room temperature (60-70°F). Avoid humidity extremes — too dry and trichomes crumble, too moist and mold develops. Use humidity control packs for flower. Keep edibles refrigerated after opening.',
      },
      {
        heading: 'Travel Storage',
        body: 'When transporting, use smell-proof, discreet containers. Keep products in original packaging with labels intact. Store in the trunk, not the passenger compartment. Never cross state lines with cannabis products, regardless of legality in either state.',
      },
    ],
  },
  {
    slug: 'cannabis-and-medications',
    category: 'safety',
    title: 'Cannabis & Medications',
    subtitle: 'Know before you mix.',
    readTime: '5 min',
    sections: [
      {
        heading: 'Drug Interactions Are Real',
        body: 'Cannabis — particularly CBD — can interact with prescription medications. CBD inhibits cytochrome P450 enzymes in the liver, the same enzymes that metabolize many common drugs. This can increase or decrease the effectiveness of those medications.',
      },
      {
        heading: 'Common Interactions',
        body: 'Blood thinners (warfarin), certain antidepressants (SSRIs), anti-seizure medications, and immunosuppressants can all be affected by cannabis use. If you take any prescription medication regularly, discuss cannabis use with your doctor.',
      },
      {
        heading: 'Alcohol & Other Substances',
        body: 'Combining cannabis with alcohol amplifies impairment and increases the risk of negative effects. Cannabis can also interact with other substances in unpredictable ways. If you choose to combine, do so cautiously and with awareness.',
      },
      {
        heading: 'The Conversation',
        body: 'Be honest with your healthcare providers about cannabis use. Medical professionals increasingly recognize cannabis as a legitimate therapeutic tool. They can\'t help you manage interactions if they don\'t know you\'re consuming. Transparency serves your health.',
      },
    ],
  },
];
